import { EGradientDirection, ESalePercentageDisplayType } from '../enums';
import { NumberConfig } from '../types';
import { useEffect, useState } from 'react';
import { BigNumber } from 'bignumber.js';
import { isAndroid, isIOS, isSafari } from 'react-device-detect';

export const createGradientOrSolid = ({
    colorOne,
    colorTwo,
    gradientDirection
}: {
    colorOne: string;
    colorTwo?: string;
    gradientDirection?: EGradientDirection;
}) => {
    if (colorOne && (colorTwo === 'false' || !colorTwo)) {
        return colorOne;
    }
    if (
        gradientDirection &&
        !Object.values(EGradientDirection).includes(gradientDirection)
    ) {
        gradientDirection = EGradientDirection.BottomToTop;
    }
    return `linear-gradient(${
        gradientDirection || EGradientDirection.BottomToTop
    }, ${colorOne}, ${colorTwo})`;
};

export function useUrlQuery(query: string) {
    const queryParameters = new URLSearchParams(window.location.search);
    return queryParameters.get(query);
}

export function useUrlHash(query: string) {
    const queryParameters = new URLSearchParams(window.location.hash);
    return queryParameters.get(query);
}

export function useApplePay() {
    const isApplePayAvailable = (window as any)?.ApplePaySession;
    return { isApplePayAvailable };
}

const formattingCoundown = (values: number[]) => {
    if (values[0] > 0) {
        return `${values[0]}d ${values[1]}h ${values[2]}m ${values[3]}s`;
    } else if (values[1] > 0) {
        return `${values[1]}h ${values[2]}m ${values[3]}s`;
    } else if (values[2] > 0) {
        return `${values[2]}m ${values[3]}s`;
    } else return `${values[3]}s`;
};

const getReturnValues = (countDown: any) => {
    const days = Math.floor(countDown / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
        (countDown % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const minutes = Math.floor((countDown % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((countDown % (1000 * 60)) / 1000);

    return formattingCoundown([days, hours, minutes, seconds]);
};

export function useCountdown(targerDate: number) {
    const [countDown, setCountDown] = useState(
        targerDate - new Date().getTime()
    );

    useEffect(() => {
        const interval = setInterval(() => {
            setCountDown(targerDate - new Date().getTime());
        }, 1000);

        return () => clearInterval(interval);
    }, [targerDate]);

    if (countDown < 0) return 'Expired';
    return getReturnValues(countDown);
}

const getDecimalPlaces = (multiplier?: number) => {
    switch (multiplier) {
        case 1:
            return 0;
        case 100:
            return 2;
        case 1000:
            return 3;
        default:
            return 2;
    }
};

export function formattingNumber(
    number: number | string,
    config?: NumberConfig,
    isCents?: boolean,
    multiplier?: number,
    isPrice?: boolean,
    abbreviateWithDecimals?: boolean
): string {
    const isBigIntSupported = typeof BigInt !== 'undefined';
    const isInteger = Number.isInteger(number);
    if (!config) return number.toLocaleString();
    let formattedNumber =
        isCents && multiplier
            ? `${(number as number) / multiplier}`
            : `${number}`;
    const decimalPlaces = getDecimalPlaces(multiplier);
    let abbreviationLetter = null;

    if (isBigIntSupported && !isPrice && !abbreviateWithDecimals && isInteger) {
        const num = BigInt(number);
        if (config.billionShorthand && num >= BigInt(1000000000)) {
            formattedNumber = (num / BigInt(1000000000)).toString();
            abbreviationLetter = 'B';
        } else if (config.millionShorthand && num >= BigInt(1000000)) {
            formattedNumber = (num / BigInt(1000000)).toString();
            abbreviationLetter = 'M';
        } else if (config.thousandShorthand && num >= BigInt(1000)) {
            formattedNumber = (num / BigInt(1000)).toString();
            abbreviationLetter = 'K';
        }
    } else {
        const num = new BigNumber(number);
        if (config.billionShorthand && num.greaterThanOrEqualTo(1000000000)) {
            formattedNumber = num.dividedBy(1000000000).toString();
            abbreviationLetter = 'B';
        } else if (
            config.millionShorthand &&
            num.greaterThanOrEqualTo(1000000)
        ) {
            formattedNumber = num.dividedBy(1000000).toString();
            abbreviationLetter = 'M';
        } else if (config.thousandShorthand && num.greaterThanOrEqualTo(1000)) {
            formattedNumber = num.dividedBy(1000).toString();
            abbreviationLetter = 'K';
        }
    }

    const parts = formattedNumber.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, config.milSeparator);

    if (isPrice || abbreviateWithDecimals) {
        if (!parts[1]) {
            if (decimalPlaces === 0) {
                parts.length = 1;
            } else {
                parts[1] = '00';
            }
        } else if (parts[1].length < decimalPlaces) {
            parts[1] = parts[1].padEnd(decimalPlaces, '0');
        } else {
            parts[1] = parts[1].substring(0, decimalPlaces);
        }
    } else {
        if (parts[1]) parts[1] = parts[1].substring(0, 2);
    }

    formattedNumber = parts.join(config.fractionalSeparator);

    if (abbreviationLetter) formattedNumber += abbreviationLetter;

    return formattedNumber;
}

const isSafeInteger = (value: string) => {
    const parsedValue = Number(value);
    return !isNaN(parsedValue) && parsedValue <= Number.MAX_SAFE_INTEGER;
};

const getSaleForNonBigInt = (
    quantity: number | string,
    sale: number,
    saleDisplayType?: ESalePercentageDisplayType
) => {
    if (saleDisplayType === ESalePercentageDisplayType.MULTIPLIER) {
        return (
            Number(quantity) / sale
        ).toString();
    }
    if (saleDisplayType === ESalePercentageDisplayType.FIXED_AMOUNT) {
        return (
            Number(quantity) - Number(sale)
        ).toString();
    }
    return Math.round(Number(quantity) / (sale / 100 + 1)).toString();
};

export function calculateSaleStrokedNumber(
    quantity: number | string,
    sale: number,
    saleDisplayType?: ESalePercentageDisplayType
) {
    const isBigIntSupported = typeof BigInt !== 'undefined';
    if (isBigIntSupported && !isSafeInteger(quantity.toString())) {
        if (saleDisplayType === ESalePercentageDisplayType.MULTIPLIER) {
            return (
                BigInt(quantity) / BigInt(sale)
            ).toString();
        }
        if (saleDisplayType === ESalePercentageDisplayType.FIXED_AMOUNT) {
            return (
                BigInt(quantity) - BigInt(sale)
            ).toString();
        }
        if (typeof quantity === 'string' && !isSafeInteger(quantity)) {
            const saleCalc =
                BigInt(sale) >= BigInt(100)
                    ? (BigInt(quantity) * BigInt(100)) /
                      (BigInt(100) + BigInt(sale))
                    : (BigInt(quantity) * BigInt(100)) /
                      (BigInt(100) - BigInt(sale));

            return saleCalc.toString();
        }
        return getSaleForNonBigInt(quantity, sale, saleDisplayType);
    } else {
        return getSaleForNonBigInt(quantity, sale, saleDisplayType);
    }
}

export const checkIfOlderOsVersions = (): boolean => {
    const ua = navigator?.userAgent?.toLowerCase();
    if (!ua) return true;

    const isChromeIOS = /crios/.test(ua);

    let versionMatch = ua.match(/version\/(\d+(\.\d+)?)/);

    if (!versionMatch) {
        versionMatch = ua.match(/crios\/(\d+(\.\d+)?)/); // Find version for Chrome
    }

    if (!versionMatch) return false; // Unable to extract version, consider it older

    const osMobileVersion = parseFloat(versionMatch[1]);
    if (isNaN(osMobileVersion)) return true; // Parsing failed, consider it older

    const SAFARI_IOS_THRESHOLD = 14.5;
    const CHROME_IOS_THRESHOLD = 92.1;
    const ANDROID_THRESHOLD = 123;

    return (
        ((isSafari || isIOS) && osMobileVersion < SAFARI_IOS_THRESHOLD) ||
        (isAndroid && osMobileVersion < ANDROID_THRESHOLD) ||
        (isChromeIOS && osMobileVersion < CHROME_IOS_THRESHOLD)
    );
};

export const percentageToMultiplier = (
    markupPercentage: number,
    amountAfterSale: number
) => {
    const multiplier = 1 + markupPercentage / 100;
    const amountBeforeSale = amountAfterSale / multiplier;
    return (amountAfterSale / amountBeforeSale).toFixed(0);
};
